import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Container,
  Tabs,
  Tab,
  CircularProgress,
  Chip,
  Divider,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Alert,
} from '@mui/material';
import { styled, alpha, keyframes } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ArticleIcon from '@mui/icons-material/Article';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkIcon from '@mui/icons-material/Link';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import ShareIcon from '@mui/icons-material/Share';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ImageIcon from '@mui/icons-material/Image';
import { fetchResources, fetchResourcesByCategory, getResourceDownloadUrl } from '../services/api';

// YouTube embed component
const YouTubeEmbed = ({ videoUrl }) => {
  // Extract video ID from various YouTube URL formats
  const getYouTubeVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const videoId = getYouTubeVideoId(videoUrl);
  if (!videoId) return <Typography color="error">Invalid YouTube URL</Typography>;

  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      paddingTop: '56.25%', // 16:9 aspect ratio
      overflow: 'hidden',
      borderRadius: 1,
    }}>
      <iframe
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          border: 0,
        }}
        src={`https://www.youtube.com/embed/${videoId}`}
        title="YouTube video player"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </Box>
  );
};

const RESOURCE_CATEGORIES = [
  "All Resources",
  "CV Templates",
  "Cover Letter Templates",
  "Study Guides",
  "Scholarship Forms",
  "Application Tips",
  "Interview Prep",
  "Other"
];

const ResourceIcon = styled(Box)(({ theme, type, filetype }) => {
  let bgColor = theme.palette.grey[700];
  let gradientColor = theme.palette.grey[600];

  if (type === 'youtube') {
    bgColor = '#FF0000'; // YouTube red
    gradientColor = '#CC0000'; // Darker YouTube red
  } else if (type === 'link') {
    bgColor = '#0288d1'; // Vibrant blue
    gradientColor = '#01579b'; // Darker blue
  } else if (filetype) {
    if (filetype.includes('pdf')) {
      bgColor = '#f44336'; // Material red
      gradientColor = '#d32f2f'; // Darker red
    } else if (filetype.includes('doc')) {
      bgColor = '#1976d2'; // Microsoft blue
      gradientColor = '#0d47a1'; // Darker blue
    }
  }

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, ${bgColor} 0%, ${gradientColor} 100%)`,
    color: theme.palette.common.white,
    borderRadius: '50%',
    padding: theme.spacing(2),
    width: 64,
    height: 64,
    flexShrink: 0,
    boxShadow: '0 6px 15px rgba(0,0,0,0.2)',
    border: '2px solid rgba(255,255,255,0.15)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 8px 20px rgba(0,0,0,0.25)'
    }
  };
});

// Display file name with proper truncation
const truncateFileName = (fileName, maxLength = 20) => {
  if (!fileName || fileName.length <= maxLength) return fileName;

  const extension = fileName.split('.').pop();
  const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));

  const truncatedName = nameWithoutExt.length > maxLength - extension.length - 3
    ? `${nameWithoutExt.substring(0, maxLength - extension.length - 4)}...`
    : nameWithoutExt;

  return `${truncatedName}.${extension}`;
};

// Global function to render resource action buttons
const renderResourceActions = (resource) => {
  switch (resource.type) {
    case 'file':
      return (
        <Button
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          href={getResourceDownloadUrl(resource._id)}
          target="_blank"
          size="large"
          sx={{
            px: 3,
            py: 1,
            fontWeight: 'bold',
            borderRadius: 2,
            boxShadow: 3
          }}
        >
          Download Now
        </Button>
      );

    case 'youtube':
      return (
        <Button
          variant="contained"
          color="error"
          startIcon={<YouTubeIcon />}
          href={resource.youtube_url}
          target="_blank"
          size="large"
          sx={{
            px: 3,
            py: 1,
            fontWeight: 'bold',
            borderRadius: 2,
            boxShadow: 3
          }}
        >
          Watch on YouTube
        </Button>
      );

    case 'link':
      return (
        <Button
          variant="contained"
          color="info"
          startIcon={<OpenInNewIcon />}
          href={resource.external_url}
          target="_blank"
          size="large"
          sx={{
            px: 3,
            py: 1,
            fontWeight: 'bold',
            borderRadius: 2,
            boxShadow: 3
          }}
        >
          Open Link
        </Button>
      );

    default:
      return null;
  }
};

const ResourceDetailDialog = ({ open, resource, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [preview, setPreview] = useState({
    loading: true,
    data: null,
    error: null
  });

  useEffect(() => {
    if (!resource) return;

    setPreview({
      loading: true,
      data: null,
      error: null
    });

    // For image files, fetch and create blob URL for preview
    if (resource.type === 'file' && resource.file_type?.includes('image')) {
      fetch(getResourceDownloadUrl(resource._id))
        .then(response => {
          if (!response.ok) {
            throw new Error('Failed to load image');
          }
          return response.blob();
        })
        .then(blob => {
          setPreview({
            loading: false,
            data: URL.createObjectURL(blob),
            error: null
          });
        })
        .catch(error => {
          setPreview({
            loading: false,
            data: null,
            error: error.message
          });
        });
    } else {
      setPreview({
        loading: false,
        data: null,
        error: null
      });
    }
  }, [resource]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Check if file is previewable
  const isPreviewable = (resource) => {
    if (!resource || resource.type !== 'file') return false;

    const fileType = resource.file_type || '';
    // Include PDFs as "previewable" to avoid duplicate containers
    return fileType.includes('image') || fileType.includes('pdf');
  };

  if (!resource) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      scroll="paper"
      aria-labelledby="resource-detail-title"
      PaperProps={{
        elevation: 5,
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle id="resource-detail-title" sx={{
        pb: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
          {resource.title}
        </Typography>
        <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Resource media/content section */}
        <Box>
          {/* YouTube Video Preview */}
          {resource.type === 'youtube' && (
            <YouTubeEmbed videoUrl={resource.youtube_url} />
          )}

          {/* PDF files - REMOVED PDF DOCUMENT SECTION */}

          {/* Image Preview */}
          {resource.type === 'file' && resource.file_type?.includes('image') && (
            <Box sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: 'grey.100',
              p: 2
            }}>
              {preview.loading ? (
                <CircularProgress />
              ) : preview.error ? (
                <Alert severity="error">{preview.error}</Alert>
              ) : preview.data ? (
                <Box
                  component="img"
                  src={preview.data}
                  alt={resource.title}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '500px',
                    objectFit: 'contain',
                    borderRadius: 1,
                    boxShadow: 1
                  }}
                />
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <ImageIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
                  <Typography>Image preview not available</Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Web Link Preview */}
          {resource.type === 'link' && (
            <Box sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              bgcolor: 'grey.100',
              p: 2
            }}>
              <Box sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                width: '100%',
                height: '400px'
              }}>
                <iframe
                  src={resource.external_url}
                  title="Web Content Preview"
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                  sandbox="allow-scripts allow-same-origin"
                />
              </Box>
              <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>
                Preview may be limited. Click "Open Link" to view the full content.
              </Typography>
            </Box>
          )}

          {/* Non-previewable file types */}
          {resource.type === 'file' && !isPreviewable(resource) && (
            <Box sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'grey.100',
              py: 6,
              px: 2
            }}>
              <Box sx={{
                width: 80,
                height: 80,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.main',
                color: 'white',
                borderRadius: '50%',
                mb: 2
              }}>
                <DescriptionIcon sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant="h6" gutterBottom>
                Preview Not Available
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 400 }}>
                This file type cannot be previewed. Please download the file to view its contents.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Resource details */}
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Chip
              label={resource.category}
              color="primary"
              size="small"
              sx={{ mr: 2 }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
              <CalendarTodayIcon sx={{ fontSize: 18, mr: 0.5 }} />
              <Typography variant="body2">
                {formatDate(resource.created_at)}
              </Typography>
            </Box>
          </Box>

          <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line', my: 3 }}>
            {resource.description}
          </Typography>

          {resource.type === 'file' && (
            <Box sx={{
              bgcolor: 'background.default',
              p: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              mt: 3
            }}>
              <ResourceIcon type={resource.type} filetype={resource.file_type} sx={{ mr: 2 }}>
                {resource.file_type?.includes('pdf') ? (
                  <PictureAsPdfIcon sx={{ fontSize: 28 }} />
                ) : (
                  <DescriptionIcon sx={{ fontSize: 28 }} />
                )}
              </ResourceIcon>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  {truncateFileName(resource.file_name)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {(resource.file_size / 1024).toFixed(1)} KB • {resource.file_type}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button
          startIcon={<ShareIcon />}
          onClick={() => {
            navigator.clipboard.writeText(window.location.href);
            alert('Link copied to clipboard!');
          }}
        >
          Share
        </Button>

        {renderResourceActions(resource)}
      </DialogActions>
    </Dialog>
  );
};

const pulse = keyframes`
  0% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.1); }
  100% { opacity: 0.3; transform: scale(1); }
`;

const Resources = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedResource, setSelectedResource] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadResources = useCallback(async () => {
    try {
      setLoading(true);

      let data;
      if (activeTab === 0) {
        // "All Resources" tab
        data = await fetchResources();
      } else {
        const category = RESOURCE_CATEGORIES[activeTab];
        data = await fetchResourcesByCategory(category);
      }

      setResources(data);
    } catch (error) {
      console.error('Error loading resources:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadResources();
  }, [loadResources]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleResourceClick = (resource) => {
    setSelectedResource(resource);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const filteredResources = searchTerm
    ? resources.filter(resource =>
      resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchTerm.toLowerCase()))
    : resources;

  const getResourceIcon = (resource) => {
    switch (resource.type) {
      case 'file':
        if (resource.file_type?.includes('pdf')) {
          return <PictureAsPdfIcon sx={{ fontSize: 28 }} />;
        } else if (resource.file_type?.includes('doc')) {
          return <ArticleIcon sx={{ fontSize: 28 }} />;
        }
        return <ArticleIcon sx={{ fontSize: 28 }} />;

      case 'youtube':
        return <YouTubeIcon sx={{ fontSize: 28 }} />;

      case 'link':
        return <LinkIcon sx={{ fontSize: 28 }} />;

      default:
        return <ArticleIcon sx={{ fontSize: 28 }} />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Helmet>
        <title>Resources | WorldWise Scholar</title>
        <meta name="description" content="Download CV templates, cover letters, study guides, and other essential resources for your international scholarship application." />
        <link rel="canonical" href="https://worldwisescholar.online/resources" />
      </Helmet>
      {/* Background Decorative Elements */}
      <Box sx={{
        position: 'absolute',
        top: '-10%',
        left: '-5%',
        width: '40%',
        height: '60%',
        background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
        zIndex: 0,
        pointerEvents: 'none',
        animation: `${pulse} 10s infinite ease-in-out`,
      }} />
      <Box sx={{
        position: 'absolute',
        top: '20%',
        right: '-10%',
        width: '50%',
        height: '70%',
        background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.08)} 0%, transparent 70%)`,
        zIndex: 0,
        pointerEvents: 'none',
        animation: `${pulse} 12s infinite ease-in-out reverse`,
      }} />

      {/* Grid Pattern */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: theme.palette.mode === 'light' ? 0.02 : 0.04,
        zIndex: 0,
        pointerEvents: 'none',
        backgroundImage: `linear-gradient(${theme.palette.divider} 1px, transparent 1px), linear-gradient(90deg, ${theme.palette.divider} 1px, transparent 1px)`,
        backgroundSize: '50px 50px',
      }} />

      <Container maxWidth="lg" sx={{ py: 8, position: 'relative', zIndex: 1 }}>
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            fontWeight="bold"
            sx={{
              background: theme.palette.mode === 'light'
                ? 'linear-gradient(to right, #111827, #4F46E5)'
                : 'linear-gradient(to right, #F8FAFC, #818CF8)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: 2,
              position: 'relative',
              display: 'inline-block'
            }}
          >
            Resources
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              maxWidth: 800,
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 400,
              opacity: 0.9
            }}
          >
            Access templates, guides, videos, and other resources to help with your scholarship
            applications and study abroad journey.
          </Typography>
        </Box>

        {/* Search and Filters */}
        <Paper sx={{
          mb: 4,
          p: 3,
          boxShadow: theme.palette.mode === 'light' ? '0 4px 20px rgba(0,0,0,0.08)' : '0 4px 20px rgba(0,0,0,0.4)',
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          background: theme.palette.mode === 'light'
            ? 'linear-gradient(to bottom, #ffffff, #f9fafc)'
            : 'linear-gradient(to bottom, #1e293b, #0f172a)'
        }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search resources..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    '& fieldset': {
                      borderColor: 'rgba(0,0,0,0.1)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(0,0,0,0.2) !important',
                    },
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                  }
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Category Tabs */}
        <Box sx={{ mb: 4 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              mb: 3,
              '& .MuiTabs-indicator': {
                backgroundColor: 'primary.main',
                height: 3,
                borderRadius: '3px 3px 0 0'
              },
              '& .MuiTab-root': {
                fontWeight: 600,
                textTransform: 'none',
                minWidth: 120,
                fontSize: '0.95rem',
                px: 3,
                '&.Mui-selected': {
                  color: 'primary.main',
                }
              }
            }}
          >
            {RESOURCE_CATEGORIES.map((category, index) => (
              <Tab
                key={category}
                label={category}
                id={`resource-tab-${index}`}
                aria-controls={`resource-tabpanel-${index}`}
              />
            ))}
          </Tabs>
        </Box>

        {/* Resources Grid */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredResources.length === 0 ? (
          <Box sx={{ textAlign: 'center', my: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchTerm ? 'No resources match your search criteria.' : 'No resources found in this category.'}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredResources.map((resource) => (
              <Grid item xs={12} md={6} lg={4} key={resource._id}>
                <Card sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  border: '1px solid rgba(0,0,0,0.04)',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 28px rgba(0,0,0,0.15)'
                  }
                }}>
                  <CardContent sx={{ p: 0, flexGrow: 1 }}>
                    {/* Top colored band based on resource type */}
                    <Box sx={{
                      height: 10,
                      bgcolor: resource.type === 'youtube'
                        ? '#FF0000'
                        : resource.type === 'link'
                          ? '#0288d1'
                          : resource.file_type?.includes('pdf')
                            ? '#f44336'
                            : '#1976d2'
                    }} />

                    {/* Header with icon and title */}
                    <Box sx={{ p: 3, pb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <ResourceIcon type={resource.type} filetype={resource.file_type}>
                          {getResourceIcon(resource)}
                        </ResourceIcon>
                        <Box sx={{ ml: 2, flex: 1 }}>
                          <Typography
                            variant="h6"
                            component="h2"
                            gutterBottom
                            fontWeight="bold"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              lineHeight: 1.3,
                              minHeight: '2.6em'
                            }}
                          >
                            {resource.title}
                          </Typography>
                          <Chip
                            label={resource.category}
                            size="small"
                            color="primary"
                            sx={{
                              fontWeight: 500,
                              borderRadius: '4px',
                              '& .MuiChip-label': {
                                px: 1.5
                              }
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>

                    {/* Resource content preview for videos */}
                    {resource.type === 'youtube' && (
                      <Box sx={{ px: 2, pb: 2 }}>
                        <Box sx={{
                          borderRadius: 2,
                          overflow: 'hidden',
                          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                        }}>
                          <YouTubeEmbed videoUrl={resource.youtube_url} />
                        </Box>
                      </Box>
                    )}

                    {/* Remove description from cards */}
                    <Box sx={{ px: 3, pb: 3 }}>
                      {/* Removed description */}

                      {/* File info if file type */}
                      {resource.type === 'file' && (
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          p: 1.5,
                          bgcolor: 'rgba(0,0,0,0.03)',
                          borderRadius: 2,
                          mt: 2,
                          border: '1px solid rgba(0,0,0,0.06)'
                        }}>
                          <Box sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: resource.file_type?.includes('pdf') ? 'rgba(244,67,54,0.08)' : 'rgba(25,118,210,0.08)',
                            mr: 1.5
                          }}>
                            {resource.file_type?.includes('pdf') ? (
                              <PictureAsPdfIcon sx={{ color: '#f44336' }} />
                            ) : (
                              <ArticleIcon sx={{ color: '#1976d2' }} />
                            )}
                          </Box>
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              maxWidth: '80%',
                              fontWeight: 500,
                              color: 'text.primary'
                            }}
                          >
                            {truncateFileName(resource.file_name)}
                          </Typography>
                        </Box>
                      )}

                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 2.5
                      }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          color: 'text.secondary'
                        }}>
                          <CalendarTodayIcon sx={{ fontSize: 14, mr: 0.5 }} />
                          <Typography variant="caption" fontWeight="medium">
                            {formatDate(resource.created_at)}
                          </Typography>
                        </Box>
                        <Button
                          variant="text"
                          color="primary"
                          size="small"
                          onClick={() => handleResourceClick(resource)}
                          sx={{
                            fontWeight: 600,
                            '&:hover': {
                              bgcolor: 'rgba(25,118,210,0.08)'
                            }
                          }}
                        >
                          View Details
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>

                  {/* Remove duplicate View Details button */}
                  <Divider />

                  {/* No more card actions here */}
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Resource Detail Dialog */}
        <ResourceDetailDialog
          open={dialogOpen}
          resource={selectedResource}
          onClose={handleCloseDialog}
        />
      </Container>
    </Box>
  );
};

export default Resources; 